import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Get OTS wallet balance
export const GetWalletBalance = createAsyncThunk(
  "otsWallet/getBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/wallet/balance`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch wallet balance" });
    }
  }
);

// Get wallet transaction ledger
export const GetWalletTransactions = createAsyncThunk(
  "otsWallet/getTransactions",
  async ({ page = 1, limit = 20, type }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/wallet/transactions`, {
        params: { page, limit, type }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch transactions" });
    }
  }
);

// Get revenue summary
export const GetRevenueSummary = createAsyncThunk(
  "otsWallet/getRevenueSummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/revenue/summary`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch revenue summary" });
    }
  }
);

// Get revenue by period
export const GetRevenueByPeriod = createAsyncThunk(
  "otsWallet/getRevenueByPeriod",
  async ({ startDate, endDate, period = 'monthly' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/revenue/by-period`, {
        params: { startDate, endDate, period }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch revenue data" });
    }
  }
);

// Get revenue breakdown (advance vs rent vs late fees)
export const GetRevenueBreakdown = createAsyncThunk(
  "otsWallet/getRevenueBreakdown",
  async ({ month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/revenue/breakdown`, {
        params: { month, year }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch revenue breakdown" });
    }
  }
);

const OTSWalletSlice = createSlice({
  name: "otsWallet",
  initialState: {
    loading: false,
    balance: {
      total: 0,
      available: 0,
      pending: 0
    },
    transactions: [],
    transactionsPagination: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    },
    revenueSummary: {
      totalRevenue: 0,
      advanceCommission: 0,
      rentCommission: 0,
      lateFees: 0,
      thisMonth: 0,
      lastMonth: 0,
      growth: 0
    },
    revenueByPeriod: [],
    revenueBreakdown: null,
    error: "",
  },
  reducers: {
    resetWalletState: (state) => {
      state.loading = false;
      state.error = "";
    },
    clearWalletError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    // Get Wallet Balance
    builder.addCase(GetWalletBalance.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetWalletBalance.fulfilled, (state, action) => {
      state.loading = false;
      state.balance = action.payload?.data || state.balance;
    });
    builder.addCase(GetWalletBalance.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch wallet balance";
    });

    // Get Wallet Transactions
    builder.addCase(GetWalletTransactions.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetWalletTransactions.fulfilled, (state, action) => {
      state.loading = false;
      state.transactions = action.payload?.data?.transactions || [];
      state.transactionsPagination = {
        currentPage: action.payload?.data?.pagination?.page || 1,
        totalPages: action.payload?.data?.pagination?.totalPages || 1,
        total: action.payload?.data?.pagination?.total || 0
      };
    });
    builder.addCase(GetWalletTransactions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch transactions";
    });

    // Get Revenue Summary
    builder.addCase(GetRevenueSummary.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetRevenueSummary.fulfilled, (state, action) => {
      state.loading = false;
      state.revenueSummary = action.payload?.data || state.revenueSummary;
    });
    builder.addCase(GetRevenueSummary.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch revenue summary";
    });

    // Get Revenue By Period
    builder.addCase(GetRevenueByPeriod.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetRevenueByPeriod.fulfilled, (state, action) => {
      state.loading = false;
      state.revenueByPeriod = action.payload?.data || [];
    });
    builder.addCase(GetRevenueByPeriod.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch revenue data";
    });

    // Get Revenue Breakdown
    builder.addCase(GetRevenueBreakdown.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetRevenueBreakdown.fulfilled, (state, action) => {
      state.loading = false;
      state.revenueBreakdown = action.payload?.data;
    });
    builder.addCase(GetRevenueBreakdown.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch revenue breakdown";
    });
  },
});

export const { resetWalletState, clearWalletError } = OTSWalletSlice.actions;
export default OTSWalletSlice.reducer;
